﻿import {getObjectUID, Paths, PathValue} from "@2d-front-monorepo/core";
import React, {ChangeEvent, EventHandler, HTMLAttributes, JSX, useCallback, useMemo, useState} from "react";
import {useDragReorder} from "../helpers/useDragReorder";
import Icon from "../Icon";
import {IMvvmContextState} from "./MvvmContextState";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {unwrapMvvmContext} from "./useMvvmTracking";

type ElementOf<T> = T extends readonly (infer U)[] ? U : never;

interface RepeaterRowProps {
	index: number;
	children: JSX.Element;
	onRemove: (index: number) => void | Promise<void>;
	removeTitle?: string;
	reorderTitle?: string;
	/** Drop handling and drag feedback from useDragReorder; its className is the feedback one. */
	rowProps: HTMLAttributes<HTMLElement>;
	/** Drag initiation from useDragReorder. Absent (no handle rendered) unless reordering is on. */
	dragProps?: HTMLAttributes<HTMLElement>;
}

function RepeaterRow(props: RepeaterRowProps): JSX.Element {
	const {index, onRemove, removeTitle, reorderTitle, rowProps, dragProps, children} = props;
	const {className, ...dropProps} = rowProps;
	const onClick = useCallback(function (e: React.MouseEvent<HTMLButtonElement>): void {
		e.preventDefault();
		void onRemove(index);
	}, [index, onRemove]);

	return <div className={className ? `widget-repeater__row ${className}` : "widget-repeater__row"} {...dropProps}>
		{dragProps && <span className="widget-repeater__row__drag" {...dragProps}
		                    aria-label={reorderTitle ?? "Reorder row"} title={reorderTitle ?? "Reorder row"}>
			<Icon icon="repeater-drag"/>
		</span>}
		<div className="widget-repeater__row__remove">
			<button type="button" onClick={onClick}>
				<Icon icon="repeater-remove-row" title={removeTitle ?? "Remove row"}/>
			</button>
		</div>
		<div className="widget-repeater__row__entry">
			{children}
		</div>
	</div>;
}

export interface MvvmRepeaterProps<
	TModel extends object,
	TProperty extends Paths<TModel>,
	TValue extends object = ElementOf<PathValue<TModel, TProperty>> & object
> {
	context: IMvvmContextState<TModel>;
	property: TProperty;
	addLabel: string;
	countLabel?: string;
	removeTitle?: string;
	/** Lets rows be reordered by dragging their handle (or focusing it and pressing the arrow
	 *  keys). Off by default: most repeaters bind a set, not a sequence, and a stray drag there
	 *  would be a silent edit. */
	reorderable?: boolean;
	/** Title of the drag handle, shown only when `reorderable`. Defaults to "Reorder row". */
	reorderTitle?: string;
	minItems?: number;
	maxItems?: number;
	childrenFactory: (item: TValue, index: number) => JSX.Element;
	addFactory?: () => TValue;
	/** Gate on the remove button: returning false (or a promise of it) keeps the row. Use it for
	 *  a row whose deletion is not obviously undoable — the confirmation belongs to the caller,
	 *  which is the only one that knows what the row costs. */
	confirmRemove?: (item: TValue, index: number) => boolean | Promise<boolean>;
}

const fixedArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];

function CopyDropdown({value, onChange}: { value: number; onChange: EventHandler<ChangeEvent<HTMLSelectElement>> }): JSX.Element {
	// Controlled so the displayed count stays in sync when addItems resets it back to 1.
	return (
		<select value={value} onChange={onChange}>{fixedArray.map((v, i) => <option key={i} value={v}>{v}</option>)}</select>
	);
}

export default function MvvmRepeater<
	TModel extends object,
	TProperty extends Paths<TModel>,
	TValue extends object = ElementOf<PathValue<TModel, TProperty>> & object
>(props: MvvmRepeaterProps<TModel, TProperty, TValue>): JSX.Element {
	const {context: contextProp, property, addLabel, removeTitle, reorderable, reorderTitle, countLabel, minItems, maxItems, childrenFactory, addFactory, confirmRemove} = props;
	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);

	useMvvmFieldSubscription(context, property, {subscribeError: false});
	const items = context.getValue<TValue[]>(property) ?? [];
	const minimumItems = minItems ?? 0;
	const maximumItems = maxItems ?? Number.MAX_SAFE_INTEGER;
	const remaining = maximumItems - items.length;

	const multiAddParts = useMemo(() => {
		const idx = addLabel.indexOf("#");
		if (idx === -1) return null;
		return [addLabel.substring(0, idx), addLabel.substring(idx + 1)] as const;
	}, [addLabel]);

	const [addCount, setAddCount] = useState(1);

	const onAddCountChange = useCallback(function (e: ChangeEvent<HTMLSelectElement>): void {
		setAddCount(Math.max(1, parseInt(e.target.value) || 1));
	}, []);

	const removeItem = useCallback(async function (index: number): Promise<void> {
		if (confirmRemove != null && !await confirmRemove(items[index], index)) {
			return;
		}
		const copy = items.toSpliced(index, 1);
		context.setValue(property, copy);
	}, [context, property, items, confirmRemove]);

	const moveItem = useCallback(function (from: number, to: number): void {
		// The keyboard handle asks for index + 1 on the last row, so past-the-end is expected.
		if (from === to || to >= items.length) {
			return;
		}
		// Immutable like addItems/removeItem: setValue is what notifies, not the mutation.
		// Move, not swap: take the row out first, then put it back at the target index.
		const copy = [...items];
		const [moved] = copy.splice(from, 1);
		copy.splice(to, 0, moved);
		context.setValue(property, copy);
	}, [context, property, items]);

	// A fixed single entry is a form, not a sequence — there is no order to change.
	const canReorder = reorderable === true && minimumItems !== maximumItems;

	const {rowProps, handleProps} = useDragReorder<TValue>(moveItem, {
		handle: true,
		disabled: !canReorder,
		rowSelector: ".widget-repeater__row",
		classNames: {dragging: "widget-repeater__row--dragging", dropTarget: "widget-repeater__row--drop-target"}
	});

	const addItems = useCallback(function (): void {
		const count = multiAddParts ? Math.min(addCount, remaining) : 1;
		const newItems = Array.from({length: count}, () => addFactory?.() ?? {} as TValue);
		const copy = [...items, ...newItems];
		context.setValue(property, copy);
		setAddCount(1);
	}, [addFactory, context, property, items, multiAddParts, addCount, remaining]);

	const klassName = useMemo(() =>
		"widget-repeater__rows"
		+ (items.length <= minimumItems ? " widget-repeater__rows--min" : "")
		+ (items.length >= maximumItems ? " widget-repeater__rows--max" : ""), [items.length, minimumItems, maximumItems]);

	const addKlassName = "widget-repeater__row widget-repeater__row--add" + (multiAddParts ? " widget-repeater__row--add-multi" : "");
	const rootKlassName = "widget-repeater " + (minimumItems === maximumItems ? "widget-repeater--single" : "widget-repeater--multiple");

	return (
		<div className={rootKlassName}>
			<div className={klassName}>
				{items.map((item, index) =>
					<RepeaterRow key={getObjectUID(item)}
					             index={index}
					             onRemove={removeItem}
					             removeTitle={removeTitle}
					             reorderTitle={reorderTitle}
					             rowProps={rowProps(item, index)}
					             dragProps={canReorder ? handleProps(index) : undefined}
					             children={childrenFactory(item, index)}/>)}
			</div>
			{items.length < maximumItems &&
                <div className={addKlassName}>
					{multiAddParts
						? <span className="widget-repeater__row--add-multi-input">
							<button type="button" onClick={addItems}>
								<Icon icon="repeater-add-row"/>
								<span>{multiAddParts[0]}</span>
							</button>
							<CopyDropdown value={addCount} onChange={onAddCountChange}/>
							<span>{multiAddParts[1]}</span>
						</span>
						: <button type="button" onClick={addItems} className="widget-repeater__row--add-single-input">
							<Icon icon="repeater-add-row"/>
							<span>{addLabel}</span>
						</button>
					}
					{countLabel && <span className="widget-repeater__row--count">{countLabel} </span>}
                </div>}
		</div>
	);
}
